import { FunctionComponent } from "react";

import Button from "@ui/components/cta/Button";
import { NoNotificationsFlag } from "@ui/components/icon/NoNotificationsFlag";
import { AvatarSize } from "@ui/components/notifications/Avatar/Avatar";
import Notification from "@ui/components/notifications/Notification/Notification";
import { useComponentConfig } from "@ui/context/component-config-context";
import useDefaults from "@ui/hooks/use-defaults";
import { AnyNotification } from "@voxmedia/duet-context/context/notifications-context";

import * as styles from "./NotificationsList.css";

export interface NotificationsListBaseConfig {
  showNoNotificationsFlag: boolean;
  showCommunityLogo: boolean;
  avatarSize: AvatarSize;
  headerSize: string;
  showLatestHeading: boolean;
  notificationClassName?: string;
  /**
   * Visual treatment for the All / Unread filter switcher. `track` renders
   * the FeedTabs-style sliding pill switcher (Verge); `default` keeps the
   * standalone outlined pill buttons used everywhere else.
   */
  filtersVariant: "default" | "track";
}

interface NotificationsListProps extends Partial<NotificationsListBaseConfig> {
  notifications: AnyNotification[];
  showLoader: boolean;
  onLoadMoreClick: () => void;
  onNotificationItemClick: (notification: AnyNotification) => Promise<void>;
  notificationsHasNext: boolean;
  loadMoreText?: string;
}

const NotificationsList: FunctionComponent<NotificationsListProps> = (
  props,
) => {
  const notificationsListConfig = useComponentConfig("notificationsList");

  const {
    notifications,
    showLoader,
    onLoadMoreClick,
    onNotificationItemClick,
    notificationsHasNext,
    loadMoreText = "See previous notifications",
    showCommunityLogo,
    avatarSize,
    showNoNotificationsFlag,
    showLatestHeading,
    notificationClassName,
  } = useDefaults(props, notificationsListConfig);

  return (
    <>
      {notifications.length > 0 && (
        <>
          {showLatestHeading && (
            <h2 className={styles.latestHeading}>Latest</h2>
          )}
          <div className={styles.notificationsList}>
            {notifications.map((notification) => (
              <Notification
                key={notification.id}
                notification={notification}
                handleNotificationClick={onNotificationItemClick}
                showCommunityLogo={showCommunityLogo}
                avatarSize={avatarSize}
                className={notificationClassName}
              />
            ))}
          </div>
        </>
      )}

      <div className={styles.paginationContainer}>
        {showLoader ? (
          <div className={styles.notificationsSpinner} />
        ) : (
          <>
            {!notificationsHasNext ? (
              <div className={styles.noNotificationsContainer}>
                {showNoNotificationsFlag && <NoNotificationsFlag />}
                <p className={styles.noNotificationsText}>
                  You&apos;re caught up!
                </p>
              </div>
            ) : (
              <Button
                className={styles.paginationButton}
                text={loadMoreText}
                displayType="secondary"
                onClick={onLoadMoreClick}
                role="button"
              />
            )}
          </>
        )}
      </div>
    </>
  );
};

export default NotificationsList;
